import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ShowFor from '@Components/ShowFor';
import { useInviteCodes } from '@Hooks/useInviteCodes';
import { useSubscriptionTiers } from '@Hooks/useSubscriptionTiers';
import type { RegistrationCode } from '@Types/registrationCode';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { Pencil, Plus, SearchCode, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteInviteCodeDialog from './DeleteInviteCodeDialog';
import InviteCodeSheet from './InviteCodeSheet';
import PromoDetailsCell from './PromoDetailsCell';

const INVITE_CODE_FIELD_MAPPING: Record<string, string> = {
  code: 'filter[code]',
  description: 'filter[promoDetails]',
  createdAt: 'filter[createdAt]',
  createdBy: 'filter[createdBy]',
};

const INVITE_CODE_STATIC_PARAMS: Record<string, string> = {
  'include[registrationCode][filter][deletedAt]': 'null',
};

const buildColumnConfigs = (
  tierTitles: Record<string, string>,
): ColumnConfig<RegistrationCode>[] => [
  {
    id: 'code',
    headerLabel: 'Name',
    accessor: (row) => row.code || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.promoDetails?.join(', ') || '-',
    type: 'text',
    cell: (info) => <PromoDetailsCell inviteCode={info.row.original} />,
    size: 280,
    enableSorting: false,
  },
  {
    id: 'allowedTiers',
    headerLabel: 'Allowed Tiers',
    accessor: (row) =>
      row.allowedSubTierIds
        ?.map((tierId) => tierTitles[tierId])
        .filter(Boolean)
        .join(', ') || '-',
    type: 'text',
    size: 200,
    enableSorting: false,
  },
  {
    id: 'createdOn',
    headerLabel: 'Created On',
    accessor: (row) => row.createdAt || '-',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return 'N/A';
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
    enableSorting: false,
  },
  {
    id: 'createdBy',
    headerLabel: 'Created By',
    accessor: (row) => row?.createdByUser?.name || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
];

export default function InviteCodesList() {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedInviteCode, setSelectedInviteCode] =
    useState<RegistrationCode | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteCodeToDelete, setInviteCodeToDelete] =
    useState<RegistrationCode | null>(null);

  const tiers = useSubscriptionTiers();
  const columnConfigs = useMemo(
    () =>
      buildColumnConfigs(
        Object.fromEntries(
          (tiers.data ?? []).map((tier) => [tier.id, tier.title]),
        ),
      ),
    [tiers.data],
  );

  const handleEditClick = (inviteCode: RegistrationCode) => {
    setSelectedInviteCode(inviteCode);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (inviteCode: RegistrationCode) => {
    setInviteCodeToDelete(inviteCode);
    setDeleteDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedInviteCode(null);
    setIsSheetOpen(true);
  };

  const handleSheetSuccess = () => {
    toastSuccess(
      selectedInviteCode
        ? `"${selectedInviteCode.code}" has been updated.`
        : 'Invite Code has been created.',
    );
  };

  const controller = useResourceTableController<RegistrationCode>({
    columnsConfig: columnConfigs,
    defaultSort: {
      id: 'code',
      desc: false,
    },
    baseSearchParams: () => ({}),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: INVITE_CODE_FIELD_MAPPING,
        staticParams: INVITE_CODE_STATIC_PARAMS,
      }),
    quickFilterFields: [{ field: 'code', label: 'Name' }],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'name'],
      right: [],
    },
    rowActions: (row) => (
      <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Edit ${row.code}`}
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Delete ${row.code}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </ShowFor>
    ),
  });

  const { data: payload, status } = useInviteCodes({
    searchParams: {
      ...controller.searchParams,
      'page[number]': controller.pageIndex + 1,
      'page[size]': controller.pageSize,
    },
    enabled: true,
  });

  const rows: RegistrationCode[] = payload?.data ?? [];
  const meta = payload?.meta;

  return (
    <>
      <ResourceTableView<RegistrationCode>
        label="Invite Codes"
        summaryIcon={<SearchCode />}
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        onRowClick={(row) => navigate(`/invite-codes/${row.id}`)}
        renderExtraActions={() => (
          <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
            <Button variant="primary" onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create Invite Code
            </Button>
          </ShowFor>
        )}
      />

      <InviteCodeSheet
        inviteCode={selectedInviteCode}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <DeleteInviteCodeDialog
        inviteCode={inviteCodeToDelete}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </>
  );
}
